import { courseExporter } from './courseExporter';
import { CourseRevisionPanel, CourseHistoryPanel } from './CourseRevisionPanel';
import { reviewStatusLabel } from './courseRevisions';
import { useParams, Navigate } from 'react-router-dom';
import React, { useState } from 'react';
import {
  ListGuesser,
  ShowGuesser,
  CreateGuesser,
  FieldGuesser,
  InputGuesser,
} from '@api-platform/admin';
import {
  TextField,
  ReferenceField,
  ReferenceArrayField,
  SelectInput,
  ReferenceInput,
  SingleFieldList,
  TranslatableInputs,
  TextInput,
  NumberInput,
  FunctionField,
  FormDataConsumer,
  ChipField,
  DateField,
  BooleanInput,
  useGetList,
  useGetIdentity,
  useNotify,
  useRefresh,
  TopToolbar,
  FilterButton,
  CreateButton,
  EditButton,
} from 'react-admin';
import { RichTextInput } from 'ra-input-rich-text';
import { Button, Chip, Grid } from '@mui/material';
import {CourseCategoriesInput, CourseCodesInput} from './Inputs';
import RewriteableEditor from './RewriteableEditor';
import { Pagination } from 'react-admin';
import {CourseTypesList, EngagementTypesList} from '../../constants/Course';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import {
  validateSeoDescription,
  validateSeoKeywords,
  validateSeoTitle,
  validateKeywords,
} from '../../validators/common';
import { validateTitle } from '../../validators/course';
import { CkEditorInput } from '../Inputs/CkEditorInput';
import { CourseActions } from './Edit';
import { isGranted } from '../../helper/security';
import { ROLES } from '../../constants/Roles';
import { Link } from 'react-router-dom';
import { getSelectedCompanyFilter } from '../../helper/filter';
import { ExportAllButton } from '../ExportAllButton';
import { getUser } from '../../helper/user';
import {
  SupplierFilter,
  SupplierInput,
} from '../Inputs/SupplierInput';

const CreatedByFilter = (props) => {
  const companyFilter = getSelectedCompanyFilter() || {};
  const { data: courses = [], isPending } = useGetList('courses', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'createdBy.email', order: 'ASC' },
    filter: companyFilter,
  });
  const choices = Array.from(
    new Set(courses.map((course) => course.createdByEmail).filter(Boolean))
  ).map((email) => ({ id: email, name: email }));

  return (
    <SelectInput
      {...props}
      choices={choices}
      emptyText="All courses"
      disabled={isPending}
    />
  );
};

export const CourseListActions = ({}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const { data } = useGetIdentity();
  const isSeo = data?.roles?.includes(ROLES.ROLE_SEO_EDITOR);

  return (
      <TopToolbar>
        <FilterButton/>
        {!isSeo && <CreateButton/>}
        {!isSeo && <ExportAllButton/>}
        {isGranted(ROLES.ROLE_BRANCH_ADMIN, data) ? (
            <Button
                startIcon={<SaveAsIcon/>}
                color="primary"
                component={Link}
                size={'small'}
                to={`/courses/generate`}
                disabled={loading}
                sx={{lineHeight: '1.5'}}
            >
              Generate
            </Button>
        ) : null}
      </TopToolbar>
  );
};

export const CourseList = (props) => {
  const user = getUser();
  const isCourseCreator = user?.roles?.includes(ROLES.ROLE_COURSE_CREATOR);
  const isSeo = user?.roles?.includes(ROLES.ROLE_SEO_EDITOR);
  const canReview = isGranted(ROLES.ROLE_ADMIN, user);
  const companyFilter = isSeo ? {} : getSelectedCompanyFilter();
  const filters = [
    <TextInput label="Code" source="code" />,
    <TextInput label="Title" source="translations.title" />,
    <TextInput label="Categories" source="categories.translations.name" />,
    <BooleanInput label="Enabled" source="enabled" />,
    <BooleanInput
      label="Check"
      source="check"
      sx={{
        minWidth: 120,
        height: 56,
        justifyContent: 'center',
        '& .MuiFormControlLabel-root': {
          height: 56,
          margin: 0,
        },
      }}
    />,
    <SelectInput label={'Type'} source="type" choices={CourseTypesList} />,
    <SupplierFilter
      source="supplier"
      company={companyFilter?.company}
    />,
    <CreatedByFilter label="Created by" source="createdBy.email" />,
  ];

  return (
    <ListGuesser
      {...props}
      filters={[
        ...(isSeo ? filters.slice(0, 2) : filters),
        ...((isSeo || canReview) ? [<BooleanInput label="Needs review" source="seoNeedsReview" alwaysOn />] : []),
      ]}
      filter={companyFilter}
      actions={<CourseListActions />}
      exporter={courseExporter}
      perPage={10}
      pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100, 500]} />}
      bulkActionButtons={isSeo ? false : undefined}
      rowClick={
        isSeo ? 'edit' : isCourseCreator
          ? (...args) =>
              args[2].editableByCourseCreator ||
              (!args[2].enabled &&
                args[2].createdByEmail === user.username)
                ? 'edit'
                : 'show'
          : undefined
      }
    >
      <FieldGuesser source={'code'} />
      <TextField source={'title'} sortBy={'translations.title'} />
      {!isSeo && <ReferenceArrayField
        label="Categories"
        source="categories"
        sortBy={'categories.translations.name'}
        reference="categories"
      >
        <SingleFieldList>
          <ChipField source="labelView" />
        </SingleFieldList>
      </ReferenceArrayField>}
      {isSeo ? <TextField source="companyName" label="Company" sortable={false} /> : <ReferenceField
        label="Company"
        source="company"
        sortBy={'company.name'}
        link={isCourseCreator ? false : 'show'}
        reference="companies"
      >
        <TextField source="name" />
      </ReferenceField>}
      {(isSeo || canReview) && <FunctionField label="Review status" sortable={false} render={record => record.reviewStatus && record.reviewStatus !== 'approved' ? <Chip size="small" color={record.reviewStatus === 'review' ? 'warning' : 'default'} label={reviewStatusLabel(record.reviewStatus)} /> : null} />}
      {!isSeo && <TextField source="supplier" label="Supplier" />}
      {!isSeo && <FunctionField
        source={'type'}
        sortBy={'type'}
        label="Type"
        render={(record) =>
          CourseTypesList.filter((item) => item.id === record.type)[0].name
        }
      />
      }
      <FieldGuesser source={'enabled'} />
      {!isSeo && <FieldGuesser source={'check'} />}
      {!isSeo && <FieldGuesser source={'priority'} />}
      {!isSeo && <DateField source={'updatedAt'} locales={'de'} showTime />}
      {!isSeo && <DateField source={'createdAt'} locales={'de'} showTime />}
      {isCourseCreator && (
        <TextField source="createdByEmail" label="Created by" />
      )}
    </ListGuesser>
  );
};

export const CourseShow = (props) => {
  const { id } = useParams();
  const { data } = useGetIdentity();
  if (getUser()?.roles?.includes(ROLES.ROLE_SEO_EDITOR)) return <Navigate to={`/courses/${encodeURIComponent(decodeURIComponent(id))}`} replace />;
  const isCourseCreator = data?.roles?.includes(ROLES.ROLE_COURSE_CREATOR);

  if (isCourseCreator) {
    return (
      <ShowGuesser actions={<CourseActions isOnEditPage={false} />} {...props}>
        <FieldGuesser source={'title'} />
        <FieldGuesser source={'code'} />
        <FieldGuesser source={'type'} />
        <FieldGuesser source={'enabled'} />
        <FieldGuesser source={'editableByCourseCreator'} />
        <TextField source="createdByEmail" label="Created by" />
        <DateField source={'createdAt'} locales={'de'} showTime />
        <DateField source={'updatedAt'} locales={'de'} showTime />
      </ShowGuesser>
    );
  }

  return (
    <ShowGuesser
      actions={
        isGranted(ROLES.ROLE_BRANCH_ADMIN, data) ? (
          <CourseActions isOnEditPage={false} />
        ) : null
      }
      {...props}
    >
      {isGranted(ROLES.ROLE_ADMIN, data) && <CourseRevisionPanel courseId={decodeURIComponent(id)} />}
      <FieldGuesser source={'title'} />
      <TextField source={'id'} />
      <FieldGuesser source={'code'} />
      <FieldGuesser source={'drupalId'} />
      <FieldGuesser source={'type'} />
      <FieldGuesser source={'multipleOfficeVersions'} />
      <ReferenceArrayField
        label="Course Cities"
        source="courseCities"
        reference="course_cities"
      >
        <SingleFieldList>
          <ChipField source="cityView" />
        </SingleFieldList>
      </ReferenceArrayField>
      <FieldGuesser source={'enabled'} />
      <FieldGuesser source={'editableByCourseCreator'} />
      <FieldGuesser source={'createdAt'} />
      <FieldGuesser source={'updatedAt'} />
      <ReferenceField label="CreatedBy" source="createdBy" reference="users">
        <TextField source="email" />
      </ReferenceField>
      <ReferenceField label="UpdatedBy" source="updatedBy" reference="users">
        <TextField source="email" />
      </ReferenceField>
      {isGranted(ROLES.ROLE_ADMIN, data) && <CourseHistoryPanel courseId={decodeURIComponent(id)} />}
    </ShowGuesser>
  );
};

export const CourseCreate = (props) => {
  const user = getUser();
  const isCourseCreator = user?.roles?.includes(ROLES.ROLE_COURSE_CREATOR);
  const canManageCourseCreatorAccess = isGranted(ROLES.ROLE_ADMIN, user);

  const transform = (data) => {
    data['basePrice'] = data.basePrice.toString();
    if (data['taxPercentage']) {
      data['taxPercentage'] = data.taxPercentage.toString();
    }
    if (isCourseCreator) {
      data['enabled'] = false;
    }
    data['translations'] = {
      de: {
        title: data.title.de,
        subtitle: data.subtitle.de,
        description: data.description.de,
        descriptionShort: data.descriptionShort.de,
        descriptionShortForList: data.descriptionShortForList.de,
        requirements: data.requirements.de,
        certificationContent: data.certificationContent.de,
        targetGroup: data.targetGroup.de,
        targetGroupShort: data.targetGroupShort.de,
        objectives: data.objectives.de,
        exam: data.exam.de,
        documents: data.documents.de,
        keywords: data.keywords.de,
        content: data.content.de,
        showUntilText: data.showUntilText.de,
        ausbildungInfo: data.ausbildungInfo.de,
        ausbildungFaq: data.ausbildungFaq.de,
        ausbildungAdditionalService: data.ausbildungAdditionalService.de,
        ausbildungAfter: data.ausbildungAfter.de,
        seoTitle: data.seoTitle.de,
        seoDescription: data.seoDescription.de,
        seoKeywords: data.seoKeywords.de,
        educationSubsidy: data.educationSubsidy.de,
        subsidy: data.subsidy.de,
        subsidyShort: data.subsidyShort.de,
        educationGraduationTitle: data.educationGraduationTitle.de,
        educationTeachingTime: data.educationTeachingTime.de,
        educationPromotion: data.educationPromotion.de,
        educationSpace: data.educationSpace.de,
        educationLabourMarketChances: data.educationLabourMarketChances.de,
        educationRatio: data.educationRatio.de,
        examCodes: data.examCodes.de,
        eduGoal: data.eduGoal.de,
        graduationType: data.graduationType.de,
        teachingType: data.teachingType.de,
        executionType: data.executionType.de,
        reviewsByTitle: data.reviewsByTitle.de,
        requirementsShort: data.requirementsShort.de,
        vacancies: data.vacancies.de,
        salary: data.salary.de,
        descriptionLong: data.descriptionLong.de,
        locale: 'de',
      },
      // en: {
      //   title: data.title.en,
      //   subtitle: data.subtitle.en,
      //   description: data.description.en,
      //   descriptionShort: data.descriptionShort.en,
      //   descriptionShortForList: data.descriptionShortForList.en,
      //   requirements: data.requirements.en,
      //   certificationContent: data.certificationContent.en,
      //   targetGroup: data.targetGroup.en,
      //   targetGroupShort: data.targetGroupShort.en,
      //   objectives: data.objectives.en,
      //   exam: data.exam.en,
      //   documents: data.documents.en,
      //   keywords: data.keywords.en,
      //   content: data.content.en,
      //   showUntilText: data.showUntilText.en,
      //   ausbildungInfo: data.ausbildungInfo.en,
      //   ausbildungFaq: data.ausbildungFaq.en,
      //   ausbildungAdditionalService: data.ausbildungAdditionalService.en,
      //   ausbildungAfter: data.ausbildungAfter.en,
      //   seoTitle: data.seoTitle.en,
      //   seoDescription: data.seoDescription.en,
      //   seoKeywords: data.seoKeywords.en,
      //   educationSubsidy: data.educationSubsidy.en,
      //   subsidy: data.subsidy.en,
      //   subsidyShort: data.subsidyShort.en,
      //   educationGraduationTitle: data.educationGraduationTitle.en,
      //   educationTeachingTime: data.educationTeachingTime.en,
      //   educationPromotion: data.educationPromotion.en,
      //   educationSpace: data.educationSpace.en,
      //   educationLabourMarketChances: data.educationLabourMarketChances.en,
      //   educationRatio: data.educationRatio.en,
      //   examCodes: data.examCodes.en,
      //   eduGoal: data.eduGoal.en,
      //   graduationType: data.graduationType.en,
      //   teachingType: data.teachingType.en,
      //   executionType: data.executionType.en,
      //   locale: 'en',
      // },
    };

    return data;
  };

  const companyFilter = getSelectedCompanyFilter() || {};
  const createProps = isCourseCreator
    ? { ...props, redirect: 'create' }
    : props;

  return (
    <CreateGuesser {...createProps} transform={transform}>
      <Grid container spacing={4} sx={{ p: 2 }}>
        <Grid item xs={6}>
          <ReferenceInput
            source="company"
            reference="companies"
            label="Company"
            filterToQuery={(searchText) => ({ name: searchText })}
          >
            <SelectInput
              fullWidth
              source="company"
              optionText="name"
              id="company"
              disabled={true}
              defaultValue={companyFilter.company}
            />
          </ReferenceInput>
          <InputGuesser fullWidth required source={'code'} />
          <SelectInput
            fullWidth
            required
            source="type"
            choices={CourseTypesList}
            id="type"
          />
            <SelectInput
                fullWidth
                source="engagementType"
                choices={EngagementTypesList}
                id="engagementType"
            />
          <FormDataConsumer>
            {({ formData, ...rest }) => {
              return <CourseCategoriesInput formData={formData} {...rest} />;
            }}
          </FormDataConsumer>
          <InputGuesser fullWidth required source={'duration'} />
          <SelectInput
            fullWidth
            required
            source="durationType"
            id={'durationType'}
            choices={[
              { id: 'hour', name: 'Stunden' },
              { id: 'day', name: 'Tage' },
              { id: 'week', name: 'Wochen' },
              { id: 'month', name: 'Monate' },
              { id: 'year', name: 'Jahre' },
            ]}
          />
          <NumberInput
            fullWidth
            required
            id={'basePrice'}
            source={'basePrice'}
          />
          <NumberInput
            fullWidth
            id={'taxPercentage'}
            source={'taxPercentage'}
          />
          <SupplierInput company={companyFilter.company} />
          <InputGuesser
            fullWidth
            id={'multipleOfficeVersions'}
            source={'multipleOfficeVersions'}
          />
          {!isCourseCreator && (
            <InputGuesser fullWidth id={'enabled'} source={'enabled'} />
          )}
          {canManageCourseCreatorAccess && (
            <BooleanInput
              fullWidth
              label="Freelancer can always edit"
              source="editableByCourseCreator"
            />
          )}
          <BooleanInput fullWidth label="Check" source="check" />
          <hr/>
          <h3>Aggregate reviews </h3>
          <TranslatableInputs
              locales={['de']}
              defaultLocale="de"
              groupKey="translations"
          >
            <TextInput
                fullWidth
                label={'Title pattern'}
                source="reviewsByTitle"
                id="reviewsByTitle"
            />
          </TranslatableInputs>
          {!isCourseCreator && (
            <FormDataConsumer>
              {({ formData, ...rest }) => {
                return <CourseCodesInput formData={formData} {...rest} />;
              }}
            </FormDataConsumer>
          )}
            <TextInput
                fullWidth
                multiline
                rows={4}
                label="Internal comment"
                source="internalComment"
                id="internalComment"
            />
        </Grid>
        <Grid item xs={6}>
          <TranslatableInputs locales={['de']} defaultLocale="de">
            <TextInput
              required
              fullWidth
              label={'Title'}
              source="title"
              id="title"
              validate={validateTitle}
            />
            <TextInput fullWidth label={'Subtitle'} source="subtitle" />
            <CkEditorInput
              name={'description'}
              multiline
              rows={3}
              label={'Description'}
              source="description"
              id="description"
            />
            <CkEditorInput
                multiline
                rows={3}
                label={'Long description'}
                source="descriptionLong"
                id="descriptionLong"
            />
            <CkEditorInput
              name={'shortDescription'}
              multiline
              rows={3}
              label={'Short Description'}
              source="descriptionShort"
              id="descriptionShort"
            />
            <RichTextInput
              label={'Short Description In List'}
              source="descriptionShortForList"
              id="descriptionShortForList"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Other Locations'}
              id="otherLocations"
              source="otherLocations"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <TextInput
              fullWidth
              label={'Teaching Type'}
              source="teachingType"
              id="teachingType"
            />
            <TextInput
              fullWidth
              label={'Execution Type'}
              source="executionType"
              id="executionType"
            />
            <RichTextInput
              label={'Requirements'}
              source="requirements"
              id="requirements"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <TextInput
              fullWidth
              label={'Subsidy'}
              source="subsidy"
              id="subsidy"
            />
            <RichTextInput
              label={'Subsidy Short'}
              source="subsidyShort"
              id="subsidyShort"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Certification Content'}
              source="certificationContent"
              id="certificationContent"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Target Group'}
              source="targetGroup"
              id="targetGroup"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <TextInput
              fullWidth
              label={'Target Group Short'}
              source="targetGroupShort"
              id="targetGroupShort"
            />
            <RichTextInput
              label={'Objectives'}
              id="objectives"
              source="objectives"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Exam'}
              id="exam"
              source="exam"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Documents'}
              id="documents"
              source="documents"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Content'}
              id="content"
              source="content"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Ausbildung Info'}
              id="ausbildungInfo"
              source="ausbildungInfo"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Ausbildung Faq'}
              id="ausbildungFaq"
              source="ausbildungFaq"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Ausbildung Additional Service'}
              source="ausbildungAdditionalService"
              id="ausbildungAdditionalService"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Ausbildung After'}
              source="ausbildungAfter"
              id="ausbildungAfter"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Education Graduation Title'}
              source="educationGraduationTitle"
              id="educationGraduationTitle"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Education Teaching Time'}
              source="educationTeachingTime"
              id="educationTeachingTime"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Education Promotion'}
              source="educationPromotion"
              id="educationPromotion"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Education Space'}
              source="educationSpace"
              id="educationSpace"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Education Labour Market Chances'}
              source="educationLabourMarketChances"
              id="educationLabourMarketChances"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <RichTextInput
              label={'Education Ratio'}
              source="educationRatio"
              id="educationRatio"
              format={(v) => (v ? v : '')}
              parse={(v) => (v === '<p><br></p>' || v === '<p></p>' || !v ? '' : v)}
            />
            <TextInput
              required
              fullWidth
              label={'Keywords'}
              source="keywords"
              id="keywords"
              validate={validateKeywords}
            />
            <TextInput
              required
              fullWidth
              label={'Seo Title'}
              source="seoTitle"
              validate={validateSeoTitle}
              id="seoTitle"
            />
            <TextInput
              required
              fullWidth
              multiline
              label={'Seo Description'}
              rows={3}
              source="seoDescription"
              validate={validateSeoDescription}
              id="seoDescription"
            />
            <TextInput
              required
              fullWidth
              multiline
              rows={3}
              label={'Seo Keywords'}
              source="seoKeywords"
              validate={validateSeoKeywords}
              id="seoKeywords"
            />
            <TextInput
              fullWidth
              label={'Education Subsidy'}
              source="educationSubsidy"
              id="educationSubsidy"
            />
            <TextInput
              fullWidth
              label={'Exam Codes'}
              source="examCodes"
              id="examCodes"
            />
            <TextInput
              fullWidth
              label={'Edu Goal'}
              source="eduGoal"
              id="eduGoal"
            />
            <TextInput
              fullWidth
              label={'Graduation Type'}
              source="graduationType"
              id="graduationType"
            />
            <TextInput
              fullWidth
              label={'Show Until Text'}
              source="showUntilText"
              id="showUntilText"
              format={(v) => (v ? v : '')}
            />
            <TextInput
                fullWidth
                label={'Requirements short'}
                source="requirementsShort"
                id="requirementsShort"
            />
            <TextInput
                fullWidth
                label={'Salary'}
                source="salary"
                id="salary"
            />
            <TextInput
                fullWidth
                label={'Vacancies'}
                source="vacancies"
                id="vacancies"
            />
          </TranslatableInputs>
        </Grid>
      </Grid>
    </CreateGuesser>
  );
};
